// Copy-to-clipboard button with transient "Tersalin" feedback.

import { useEffect, useRef, useState } from "react";
import { Glyph } from "@/ui";
import { Button, type ButtonProps } from "@/ui";

interface CopyButtonProps extends Omit<ButtonProps, "onClick" | "children" | "asChild"> {
  value: string;
  label?: string;
  copiedLabel?: string;
}

export function CopyButton({
  value,
  label = "Salin",
  copiedLabel = "Tersalin",
  variant = "secondary",
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard API unavailable (insecure context) — fall back to a hidden textarea.
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(ta);
      }
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1800);
  }

  return (
    <Button variant={variant} {...rest} onClick={copy}>
      {copied ? <Glyph name="check" /> : <Glyph name="copy" />}
      {copied ? copiedLabel : label}
    </Button>
  );
}
